import { getAccessToken } from '../authentication/supabaseService';
import { RevenueSourceType } from '../../types/RevenueSourceType';
import { SupportedLanguage } from './recipesService';

export type RecipeAutomationType = 'RECIPE_COMPONENTS' | 'FULL_RECIPES' | 'ALLERGIES_IMPORT';

export interface AllergyMatchResult {
  fileMenuItem: string;
  matchedRecipe: string | null;
  matchType: 'exact' | 'word_overlap' | 'none';
  matchScore?: number;
  allergies: string[];
}

export interface RecipeAutomationResponse {
  success: boolean;
  message: string;
  duration?: number;
  // Allergy import specific
  totalItems?: number;
  recipesUpdated?: number;
  allergyMatches?: AllergyMatchResult[];
  notMatched?: string[];
  // RECIPE_COMPONENTS: number of dishes assigned to the passed menu
  itemsAssignedToMenu?: number;
}

/**
 * Process recipe automation - upload a Word document and create recipes or recipe components
 * Now supports multi-language translation with sourceLanguage parameter
 */
export const processRecipeAutomation = async (
  restaurantId: string,
  file: File,
  automationType: RecipeAutomationType,
  revenueSourceType: RevenueSourceType | 'ALL',
  sourceLanguage: SupportedLanguage = 'en',
  menuId?: string
): Promise<RecipeAutomationResponse> => {
  const formData = new FormData();
  formData.append('restaurantId', restaurantId);
  formData.append('file', file);
  formData.append('automationType', automationType);
  formData.append('revenueSourceType', revenueSourceType);
  formData.append('sourceLanguage', sourceLanguage);
  if (menuId) {
    formData.append('menuId', menuId);
  }

  const accessToken = await getAccessToken();

  const response = await fetch('/api/automation/recipe-automation', {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    // `details` carries the actionable message (e.g. which dishes/ingredients to fix
    // in the Word document); `error` is only the generic headline.
    throw new Error(data.details || data.error || 'Failed to process recipe file');
  }

  return data;
};
